import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RotateCcw } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import type { Brand } from "@shared/schema";

type WorkflowSource = "default" | "brand override";
type WorkflowSummary = {
  url: string;
  workflowId: string | null;
  source: WorkflowSource;
};
type PersonaWorkflowConfigResponse = {
  brandId: string;
  config: {
    profileSimUrl: string | null;
    starterSimUrl: string | null;
    updatedAt: string | null;
    updatedBy: string | null;
  } | null;
  profile: WorkflowSummary;
  starter: WorkflowSummary;
  updatedByName: string | null;
};

type ConfigField = "profileSimUrl" | "starterSimUrl";
type SaveVariables = {
  brandId: string;
  values: Partial<Record<ConfigField, string | null>>;
};

const queryKey = (brandId: string) => [
  "/api/admin/brands",
  brandId,
  "persona-workflow-config",
];

function formatUpdatedAt(value: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function PersonaWorkflowConfigCardOwner({ brand }: { brand: Brand }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [profileUrl, setProfileUrl] = useState("");
  const [starterUrl, setStarterUrl] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const dirtyFields = useRef<Record<ConfigField, boolean>>({
    profileSimUrl: false,
    starterSimUrl: false,
  });
  const endpoint = `/api/admin/brands/${brand.id}/persona-workflow-config`;
  const configQuery = useQuery<PersonaWorkflowConfigResponse>({
    queryKey: queryKey(brand.id),
    queryFn: async () => {
      const response = await fetch(endpoint, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to load persona workflow config");
      return response.json();
    },
  });

  useEffect(() => {
    const data = configQuery.data;
    if (!data || data.brandId !== brand.id) return;
    if (!dirtyFields.current.profileSimUrl) {
      setProfileUrl(data.config?.profileSimUrl ?? "");
    }
    if (!dirtyFields.current.starterSimUrl) {
      setStarterUrl(data.config?.starterSimUrl ?? "");
    }
    setHydrated(true);
  }, [configQuery.data, brand.id]);

  const saveMutation = useMutation({
    mutationFn: async ({ brandId, values }: SaveVariables) => {
      const response = await apiRequest(
        "PUT",
        `/api/admin/brands/${brandId}/persona-workflow-config`,
        values,
      );
      return response.json() as Promise<PersonaWorkflowConfigResponse>;
    },
    onSuccess: (data, variables) => {
      if (data.brandId === variables.brandId) {
        queryClient.setQueryData(queryKey(variables.brandId), data);
        if (Object.prototype.hasOwnProperty.call(variables.values, "profileSimUrl")) {
          dirtyFields.current.profileSimUrl = false;
          setProfileUrl(data.config?.profileSimUrl ?? "");
        }
        if (Object.prototype.hasOwnProperty.call(variables.values, "starterSimUrl")) {
          dirtyFields.current.starterSimUrl = false;
          setStarterUrl(data.config?.starterSimUrl ?? "");
        }
      }
      queryClient.invalidateQueries({ queryKey: queryKey(variables.brandId) });
      toast({ title: "Persona workflows saved", description: `${brand.name} is using the updated workflow configuration.` });
    },
    onError: (error: Error) =>
      toast({ title: "Save failed", description: error.message, variant: "destructive" }),
  });

  const isSaving = saveMutation.isPending;
  const canEdit = configQuery.isSuccess && hydrated && configQuery.data?.brandId === brand.id && !isSaving;
  const config = configQuery.data?.config;
  const updatedAt = formatUpdatedAt(config?.updatedAt ?? null);

  const clearField = (field: ConfigField) => {
    saveMutation.mutate({ brandId: brand.id, values: { [field]: null } });
  };

  return (
    <Card data-testid="card-persona-workflow-config">
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Persona workflows</CardTitle>
        <CardDescription>
          Choose the Sim workflows used to create the persona profile and starter personas for this brand.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {configQuery.isLoading || (!configQuery.isError && (!configQuery.data || !hydrated)) ? (
          <div className="space-y-4" aria-label="Loading persona workflow configuration">
            {[0, 1].map((item) => (
              <div key={item} className="space-y-2">
                <div className="h-4 w-48 animate-pulse rounded bg-muted" />
                <div className="h-9 animate-pulse rounded-md bg-muted" />
              </div>
            ))}
          </div>
        ) : configQuery.isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4" role="alert">
            <p className="text-sm font-medium">Persona workflows could not be loaded.</p>
            <p className="mt-1 text-xs text-muted-foreground">{configQuery.error.message}</p>
            <Button
              className="mt-3"
              size="sm"
              variant="outline"
              onClick={() => configQuery.refetch()}
              disabled={configQuery.isFetching}
              data-testid="button-retry-persona-workflows"
            >
              {configQuery.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
              Retry
            </Button>
          </div>
        ) : (
          <>
            <WorkflowField
              id="persona-profile"
              label="Persona profile workflow URL"
              helper="Accepted paths: /api/workflows/<id>/execute or /api/v2/workflows/<id>/execute on https://www.sim.ai"
              value={profileUrl}
              onChange={(value) => {
                dirtyFields.current.profileSimUrl = true;
                setProfileUrl(value);
              }}
              summary={configQuery.data.profile}
              disabled={!canEdit}
              saving={isSaving}
              onClear={() => clearField("profileSimUrl")}
              hasOverride={Boolean(config?.profileSimUrl?.trim())}
            />
            <WorkflowField
              id="starter-personas"
              label="Starter personas workflow URL"
              helper="Expected URL: https://www.sim.ai/api/v2/workflows/<id>/execute"
              value={starterUrl}
              onChange={(value) => {
                dirtyFields.current.starterSimUrl = true;
                setStarterUrl(value);
              }}
              summary={configQuery.data.starter}
              disabled={!canEdit}
              saving={isSaving}
              onClear={() => clearField("starterSimUrl")}
              hasOverride={Boolean(config?.starterSimUrl?.trim())}
            />
            <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-muted-foreground" data-testid="text-persona-workflows-updated">
                {configQuery.data.updatedByName || updatedAt ? (
                  <>
                    Last updated{configQuery.data.updatedByName ? ` by ${configQuery.data.updatedByName}` : ""}
                    {updatedAt ? ` · ${updatedAt}` : ""}
                  </>
                ) : (
                  "No brand-specific changes yet"
                )}
              </div>
              <Button
                onClick={() =>
                  saveMutation.mutate({
                    brandId: brand.id,
                    values: {
                      profileSimUrl: profileUrl.trim() || null,
                      starterSimUrl: starterUrl.trim() || null,
                    },
                  })
                }
                disabled={!canEdit}
                data-testid="button-save-persona-workflows"
              >
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function WorkflowField({
  id,
  label,
  helper,
  value,
  onChange,
  summary,
  disabled,
  saving,
  onClear,
  hasOverride,
}: {
  id: string;
  label: string;
  helper: string;
  value: string;
  onChange: (value: string) => void;
  summary: WorkflowSummary;
  disabled: boolean;
  saving: boolean;
  onClear: () => void;
  hasOverride: boolean;
}) {
  return (
    <section className="space-y-2" data-testid={`persona-workflow-field-${id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`input-${id}`} className="text-sm font-medium">{label}</label>
        <Badge variant={summary.source === "default" ? "secondary" : "outline"}>
          {summary.source === "default" ? "Default" : "Brand override"}
        </Badge>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id={`input-${id}`}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={helper.replace("Expected URL: ", "")}
          disabled={disabled}
          data-testid={`input-${id}-url`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={onClear}
          disabled={disabled || saving || !hasOverride}
          data-testid={`button-clear-${id}`}
        >
          Clear
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{helper}</p>
      <p className="text-xs text-muted-foreground">
        Effective workflow ID: <span className="font-mono">{summary.workflowId || "Not available"}</span>
      </p>
    </section>
  );
}

export function PersonaWorkflowConfigCard({ brand }: { brand: Brand }) {
  return <PersonaWorkflowConfigCardOwner key={brand.id} brand={brand} />;
}
